/* video-duo.js — runtime source selection for sections/section-video-duo.liquid.
 *
 * The encoder's contract, implemented here:
 *
 * 1. NO <source media="..."> anywhere. Safari ignores the media attribute on
 *    <source> and will download the wrong file, or both. Exactly ONE source is
 *    chosen at runtime — matchMedia('(max-width: 767px)') gets the mobile file,
 *    everything else the desktop file — then src is set and load() called.
 *    A device never ships both files.
 * 2. The markup carries no src at all, so nothing can download at parse time.
 *    The poster is the only paint until this script commits a source, which
 *    keeps the video itself out of the LCP entirely.
 * 3. IntersectionObserver (rootMargin 200px) defers the download until the band
 *    is near the viewport, pauses playback when it scrolls out, resumes on
 *    return. No point decoding video nobody can see.
 * 4. prefers-reduced-motion: reduce -> never autoplay, poster only.
 * 5. navigator.connection.saveData, or effectiveType 2g / slow-2g -> stay on
 *    the poster: a 7MB background film is exactly what Save-Data opted out of.
 *
 * Breakpoint-crossing policy: the source choice is PER PAGEVIEW once committed.
 * Swapping src on a live <video> forces a reload — the frame goes blank and the
 * loop restarts from zero, a visible restart the encoder ruled out. Doing it
 * invisibly needs two buffered <video> elements, i.e. both files on one device,
 * which is the exact thing rule 1 forbids. So: matchMedia is evaluated at the
 * moment of commit (first intersection), which naturally honours any resizes
 * before the video is reached; after commit the choice is final. In practice
 * the only crossing is a phone rotating, and keeping the mobile file on a
 * rotated phone is correct anyway.
 */
(function () {
  'use strict';

  // The section prints its <script> tag once per instance. Two instances mean
  // two identical tags, and identical external scripts each execute. One
  // global flag keeps the listeners single; a per-element marker (below) keeps
  // each <video> initialised once however many times a scan runs.
  if (window.__videoDuoInit) return;
  window.__videoDuoInit = true;

  var MOBILE_MQ = '(max-width: 767px)'; // the encoder's breakpoint, fixed

  function posterOnly() {
    // Rules 4 and 5: reduced motion or a constrained connection -> no video.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
    var c = navigator.connection;
    return !!c && (c.saveData === true || c.effectiveType === '2g' || c.effectiveType === 'slow-2g');
  }

  function init(video) {
    if (video.dataset.videoDuoReady) return; // already wired (editor re-scan, duplicate section)
    video.dataset.videoDuoReady = 'true';

    if (posterOnly()) {
      video.removeAttribute('autoplay'); // belt and braces: no src is ever assigned either
      return;
    }

    var committed = false;

    function commit() {
      // Rule 1: pick ONE file, at the moment the video is first needed.
      var wantMobile = window.matchMedia(MOBILE_MQ).matches;
      var src = (wantMobile ? video.dataset.mobileSrc : video.dataset.desktopSrc)
        || video.dataset.desktopSrc || video.dataset.mobileSrc; // fall back if one URL was cleared in the editor
      if (!src) return;
      committed = true;
      video.muted = true; // Safari quirk: the content attribute does not reliably set the IDL property, and iOS refuses inline autoplay unless muted is true
      video.src = src;
      video.load();
    }

    function playSafe() {
      var p = video.play();
      if (p && p.catch) p.catch(function () {}); // autoplay refusal just leaves the poster — never throw
    }

    if (!('IntersectionObserver' in window)) { // vanishingly rare now; load eagerly rather than never
      commit();
      playSafe();
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          if (!committed) commit(); // rule 3: download only when near the viewport
          playSafe();
        } else if (committed) {
          video.pause(); // off-screen video burns battery decoding invisible frames
        }
      });
    }, { rootMargin: '200px 0px' }); // start the fetch ~200px early so playback is ready on arrival

    video.__videoDuoObserver = io; // reachable by the section:unload cleanup below
    io.observe(video);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('video[data-video-duo]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document); // defer script, DOM already parsed
  }

  // Theme editor: sections re-render without a page load.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();

// Editor hygiene: a section unload leaves this file's IntersectionObservers
// holding the dead video. Disconnect anything scoped to the unloaded section.
document.addEventListener('shopify:section:unload', function (e) {
  var root = e.target || document;
  Array.prototype.forEach.call(root.querySelectorAll('video[data-video-duo-ready]'), function (v) {
    try { v.pause(); } catch (err) { /* already gone */ }
    if (v.__videoDuoObserver) { v.__videoDuoObserver.disconnect(); v.__videoDuoObserver = null; }
  });
});
